/**
 * API data normalizer or transformation layer
 */

import * as parsers from './parsers'

/**
 * Leagues transformer
 * @param  {Object} res    Result from API
 * @param  {Object} edition   edition config
 * @param  {Object} params    Request oarams
 * @return {Object}           leagues and current league
 */
export function onGetLeagues({ res, edition = {}, params = {} }) {
  return parsers.common.leagues.processData(res, edition, params)
}

export function onGetHeadlines({ res = {}, edition = {} }) {
  const data = res && res.data || {}
  const columns = data && data.columns || {}
  const rightColumn = parsers.flex.getColumnByName(columns, 'rightcolumn')

  const items = rightColumn && rightColumn.items || []
  const module = parsers.flex.getModuleByType(items, 'index-feed')
  const feed = module && module.feed || []
  const newsItems = parsers.flex.buildNewsItems(feed, 6, edition.domain)

  return { newsItems }
}

/**
 * Standings transformer
 * @param  {Object} res         Result from API
 * @param  {Object} edition     Edition config
 * @param  {Object} params      Request params
 * @param  {Object} location    Router location
 * @param  {Object} appProps  App props
 * @return {Object}             Standings, seasons, currentSeason, leagueTitle
 */
export function onGetStandings({ res = {}, leagueRes = {}, edition = {}, params = {}, location = {}, appProps = {} }) {
  return parsers.standings.processData( res, leagueRes, edition, params, location, appProps )
}

/**
 * @param  {Object} res Result from the API
 * @param  {Object} edition   edition config
 * @param  {Object} params    Request Params
 * @param  {Object} location  Router location
 * @param  {Object} appProps  App props
 * @return {Object}           leagues and date
 */
export function onGetScoreboard({ res = {}, edition = {}, params = {}, location = {}, appProps = {}, topEvents=[] }) {
  // TODO: call game specific parsers based on params.sport
  return parsers.cricket.scoreboard.processData(res, edition, params, location, appProps, topEvents)
}

/**
 * @param  {Object} res Result from the API
 * @param  {Object} edition   edition config
 * @param  {Object} params    Request Params
 * @return {Object}           leagues and date
 */
export function onGetTopEventScores({ res = {}, edition = {} }) {
  // TODO: call game specific parsers based on params.sport
  return parsers.cricket.scoreboard.processTopEventScores(res, edition)
}


/**
 * @param  {Object} res Result from the API
 * @param  {Object} edition   edition config
 * @param  {Object} params    Request Params
 * @param  {Object} location  Router location
 * @param  {Object} appProps  App props
 * @return {Object}           leagues and date
 */
export function onGetGamePackage({ res = {}, edition = {}, params = {}, location = {}, appProps = {} }) {
  // TODO: call game specific parsers based on params.sport
  switch(params.sport) {
    case 'cricket':
      return parsers.cricket.gamePackage.processData(res, edition, params, location, appProps)
    case 'rugby-league':
    case 'rugby':
      return parsers.rugby.gamePackage.processData(res, edition, params, location)
    default:
      return {}
  }
}

export function onGetGeoLocation({ res = {}, edition = {}, params = {}, query = {} }) {
  return parsers.common.common.getCountryFromGeoLocation(res, edition, params, query)
}

/**
 * Process play by play
 * @param  {Object} res       request response
 * @param  {Object} edition   edition config
 * @param  {Object} params    router params
 * @param  {Object} location  Router location
 * @return {Object}           action.data to be comsumed by reducers
 */
export function onGetPlayByPlay({ res = {}, edition = {}, params = {}, location = {} }) {
  // TODO: call game specific parsers based on params.sport
  return parsers.cricket.gamePackage.processPlayByPlay(res, edition, params, location)
}

/**
 * Process play by play
 * @param  {Object} res       request response
 * @param  {Object} edition   edition config
 * @param  {Object} params    router params
 * @param  {Object} location  Router location
 * @return {Object}           action.data to be comsumed by reducers
 */
export function onGetVideos({ res = {}, edition = {}, params = {}, location = {} }) {
  // TODO: call game specific parsers based on params.sport
  return parsers.cricket.gamePackage.processVideos(res, edition, params, location)
}

  /**
 * Process play by play
 * @param  {Object} scheduleRes   Request response for schedule
 * @param  {Object} leagueRes     Request response for leagues
 * @param  {Object} displayDate   Request response for calculated display date
 * @param  {Object} edition       Edition config
 * @param  {Object} params        Router params
 * @param  {Object} location      Router location
 * @return {Object}               Action.data to be comsumed by reducers
 */
export function onGetSchedule({ scheduleRes = {}, leagueRes = {}, displayDate = '', edition = {}, params = {}, location = {} }) {
  // TODO: call game specific parsers based on params.sport
  return parsers.schedule.processData( scheduleRes, leagueRes, displayDate, edition, params, location )
}

/**
 * [onGetClubhouse Clubhouse page]
 * @param  {[type]} res      [Response from Flex API]
 * @param  {Object} edition  [edition details]
 * @param  {Object} params   [params object]
 * @param  {Object} location [location details]
 * @return {[type]}          [return formatted flex API]
 */
export function onGetClubhouse({ res = {}, edition = {}, params = {}, location={} }) {

  let data = res && res.data || {}
  let columns = data && data.columns || {}
  let teamData = data && data.team || {}

  const leftColumnFeed = parsers.flex.getColumnByName(columns, 'leftcolumn')
  const rightColumnFeed = parsers.flex.getColumnByName(columns, 'rightcolumn')
  const team = parsers.flex.getClubhouseTeam(teamData, params)
  let schedule = parsers.flex.getSchedules(leftColumnFeed, edition, params, team)
  let standings = parsers.flex.getStandings(rightColumnFeed)
  let standingsFeed = standings && standings.feed || {}
  let standingsLeague = standings && standings.league || {}
  let leaders = parsers.flex.getLeaders(rightColumnFeed)
  let meta = {}

  let leftColumn = {
    name: leftColumnFeed.name || '',
    description: leftColumnFeed.description || '',
    schedule: {}
  }
  let rightColumn = {
    name: rightColumnFeed.name || '',
    description: rightColumnFeed.description || '',
    standings: {},
    leaders: {}
  }
  if(params.sport === 'rugby') {
    rightColumn.teamRankings = parsers.rugby.clubhouse.modules.getTeamRankings(standings, team)
    standings = parsers.rugby.clubhouse.modules.getStandings(standingsFeed, standingsLeague , params)
    leaders = parsers.rugby.clubhouse.modules.getTopScorers(leaders, team, params)
    meta = parsers.rugby.clubhouse.getMetaData(edition, params, team, location)
  }
  if(params.sport == 'cricket') {
    standings = parsers.cricket.gamePackage.modules.getStandings(standingsFeed, params)
  }

  leftColumn.schedule = schedule
  rightColumn.standings = standings
  rightColumn.leaders = leaders

  let loadNowAPI = false
  const middleColumn = getMidColFeed(res, edition, params, loadNowAPI)
  return { leftColumn, middleColumn, rightColumn, team, meta }

}

export function getMoreMidColFeed(res, edition, params, currentArticleCount) {
  let loadNowAPI = true
  const middleColumn = getMidColFeed(res, edition, params, currentArticleCount, loadNowAPI)
  return { middleColumn }
}

export function getMidColFeed(res = {}, edition = {}, params = {}, currentArticleCount, loadNowAPI) {
  let data = res && res.data || {}
  let columns = data && data.columns || {}
  let middleColumn = loadNowAPI ? data : parsers.flex.getColumnByName(columns, 'middlecolumn')
  let newsfeed = parsers.flex.getNewsFeed(middleColumn, params.sport, edition.domain, params.pageType, currentArticleCount, loadNowAPI) || {}
  let totalResultsCount = newsfeed.resultsCount //total counts unfiltered items for this team
  let currentCount = newsfeed.length //count the items in current array, this array has items only those are included types
  let totalCount = newsfeed.countBF
  let excludedCount = (totalCount - currentCount) //count those are not included

  return {
    newsfeed : newsfeed,
    resultsCount : newsfeed.resultsCount,
    resultsOffset : newsfeed.resultsOffset,
    hasMore : (totalResultsCount > totalCount) ? true : false,
    page : 1,
    excludedCount : excludedCount,
    totalCount : totalCount,
    sport : params.sport
  }
}



// WEBPACK FOOTER //
// ../app/lib/transform/index.js